<template>
  <div class="qr-tool">
    <!-- Upload -->
    <div class="editor-pane" v-if="!result && !error && !loading">
      <div class="pane-header">
        <span class="pane-dot input-dot" /><span class="pane-label">{{ $t('tool.QrDecoder.title') }}</span>
        <span class="pane-hint">PNG / JPEG · Max 50MB</span>
      </div>
      <el-upload ref="upload" drag action="#" :auto-upload="false" :on-change="handleFile" :limit="1" accept="image/*" :file-list="fileList">
        <i class="el-icon-picture-outline" style="font-size:42px;color:#c0c4cc" />
        <div class="el-upload__text">{{ $t('tool.QrDecoder.dropText') }}<em>{{ $t('tool.QrDecoder.clickUpload') }}</em></div>
      </el-upload>
      <div style="text-align:center;margin-top:14px" v-if="file">
        <span class="qr-file-name"><i class="el-icon-document" /> {{ file.name }}</span>
        <el-button type="primary" size="small" :loading="loading" @click="decode" style="margin-left:10px">{{ $t('tool.QrDecoder.btnDecode') }}</el-button>
      </div>
    </div>

    <!-- Loading -->
    <div class="editor-pane qr-state-pane" v-if="loading">
      <i class="el-icon-loading qr-state-icon" />
      <p class="qr-state-text">{{ $t('tool.QrDecoder.processing') }}</p>
    </div>

    <!-- Result -->
    <div class="qr-result" v-if="result">
      <div class="qr-result-hd">
        <span class="pane-dot output-dot" />
        <span class="pane-label">{{ $t('tool.QrDecoder.title') }}</span>
      </div>
      <div class="qr-result-body">
        <code class="qr-result-code">{{ result }}</code>
      </div>
      <div class="qr-result-actions">
        <el-button v-if="isUrl" size="small" type="primary" icon="el-icon-link" @click="openUrl">{{ $t('common.action.open') }}</el-button>
        <el-button size="small" icon="el-icon-document-copy" @click="doAction('copy')">{{ $t("common.action.copy") }}</el-button>
        <el-button size="small" plain @click="reset">{{ $t('tool.QrDecoder.reupload') }}</el-button>
      </div>
    </div>

    <!-- Error -->
    <div class="editor-pane qr-state-pane" v-if="error">
      <i class="el-icon-warning qr-state-icon qr-state-err" />
      <p class="qr-state-text qr-state-err-text">{{ error }}</p>
      <el-button size="small" @click="reset" style="margin-top:8px">重试</el-button>
    </div>
  </div>
</template>

<script>
let jsqrLoaded = false

export default {
  name:'QrDecoderTool',props:{config:{type:Object,default:()=>({})}},
  data:()=>({file:null,fileList:[],loading:false,result:'',error:''}),
  computed:{
    isUrl(){ return /^https?:\/\//i.test(this.result) }
  },
  methods:{
    handleFile(f){
      if(f.size>50*1024*1024){this.$message.error('最大50M');return}
      this.file=f.raw;this.fileList=[f];this.result='';this.error=''
    },
    async decode(){
      if(!this.file)return;this.loading=true;this.error=''
      try{
        const url=URL.createObjectURL(this.file)
        const img=new Image()
        await new Promise((resolve,reject)=>{img.onload=resolve;img.onerror=()=>reject(new Error('图片加载失败'));img.src=url})
        const c=document.createElement('canvas');c.width=img.width;c.height=img.height
        const ctx=c.getContext('2d',{willReadFrequently:true});ctx.drawImage(img,0,0)

        if('BarcodeDetector' in window){
          try{
            const bitmap=await createImageBitmap(c)
            const detector=new BarcodeDetector({formats:['qr_code']})
            const barcodes=await detector.detect(bitmap)
            bitmap.close()
            if(barcodes.length>0){this.result=barcodes[0].rawValue;this.loading=false;URL.revokeObjectURL(url);return}
          }catch(e){}
        }

        const imageData=ctx.getImageData(0,0,c.width,c.height)
        if(!jsqrLoaded){
          await new Promise((resolve,reject)=>{
            const s=document.createElement('script')
            s.src='https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.min.js'
            s.onload=()=>{jsqrLoaded=true;resolve()}
            s.onerror=reject
            document.head.appendChild(s)
          })
        }
        const code=window.jsQR(imageData.data,c.width,c.height)
        URL.revokeObjectURL(url)
        if(code){this.result=code.data;this.loading=false;return}
        this.error='未检测到二维码，请确认图片包含有效二维码'
      }catch(e){this.error='识别失败: '+(e.message||'请使用 Chrome/Edge 浏览器')}
      this.loading=false
    },
    openUrl(){ window.open(this.result,'_blank') },
    reset(){this.file=null;this.fileList=[];this.result='';this.error='';this.loading=false},
    doAction(a){if(a==='copy'&&this.result){navigator.clipboard.writeText(this.result);this.$message.success(this.$t("common.status.copied"))};if(a==='clear')this.reset()}
  }
}
</script>

<style scoped>
.qr-tool { display:flex;flex-direction:column;gap:16px; }

.editor-pane { background:#fafbfc;border-radius:12px;border:1px solid #ebeef5;padding:16px 20px; min-width:0; overflow:hidden; }
.pane-header { display:flex;align-items:center;gap:8px;margin-bottom:12px; flex-wrap:wrap; }
.pane-dot { width:8px;height:8px;border-radius:50%;flex-shrink:0; }
.input-dot { background:#4e7cff;box-shadow:0 0 6px rgba(78,124,255,.4); }
.output-dot { background:#43e97b;box-shadow:0 0 6px rgba(67,233,123,.4); }
.pane-label { font-size:13px;font-weight:700;color:#1a1f36; }
.pane-hint { font-size:11px;color:#b0b8c4;margin-left:auto; white-space:nowrap; }
.qr-file-name { font-size:13px;color:#606266; }

.qr-tool ::v-deep .el-upload { width:100%; }
.qr-tool ::v-deep .el-upload-dragger { display:flex;flex-direction:column;align-items:center;justify-content:center;min-height:170px; max-width:100%; width:100%; box-sizing:border-box; }

/* State panes */
.qr-state-pane { display:flex;flex-direction:column;align-items:center;padding:36px 20px;text-align:center; }
.qr-state-icon { font-size:36px;color:#4e7cff; }
.qr-state-text { margin:10px 0 0;font-size:14px;color:#606266; }
.qr-state-err { color:#f56c6c; }
.qr-state-err-text { color:#f56c6c; }

/* Result */
.qr-result { background:#f0f9eb;border:1px solid #e1f3d8;border-radius:12px;padding:20px 24px; }
.qr-result-hd { display:flex;align-items:center;gap:8px;margin-bottom:14px; }
.qr-result-body { background:#fff;border:1px solid #e4e7ed;border-radius:8px;padding:16px 20px;min-height:56px; }
.qr-result-code { font-family:'JetBrains Mono','Fira Code','Consolas',monospace;font-size:15px;color:#1a1f36;word-break:break-all;white-space:pre-wrap;line-height:1.6; }
.qr-result-actions { display:flex;gap:8px;margin-top:14px; }

@media (max-width: 640px) {
  .editor-pane { padding:12px 14px; }
  .pane-hint { display:none; }
  .qr-tool ::v-deep .el-upload-dragger { min-height:140px;padding:20px 12px; }
  .qr-tool ::v-deep .el-upload__text { font-size:12px; }
  .qr-result { padding:14px 16px; }
  .qr-result-code { font-size:13px; }
}
</style>
